<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

import { calcRoomHeatLoss } from '../lib/legacy-room-calculator.js'
import { getRoomTables } from '../lib/room-tables'
import { trackEvent } from '../lib/tracking'
import ActionButton from './ActionButton.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  inline: {
    type: Boolean,
    default: false,
  },
  showClose: {
    type: Boolean,
    default: true,
  },
  closeOnUse: {
    type: Boolean,
    default: true,
  },
  strings: {
    type: Object,
    required: true,
  },
  market: {
    type: String,
    default: 'UKMyson',
  },
})

const isWidgetMode = () => typeof window !== 'undefined' && window.__WIDGET_MODE__ === true

const emit = defineEmits(['update:modelValue', 'use'])

const copy = computed(() => props.strings?.roomCalc ?? {})

const isOpen = computed(() => (props.inline ? true : props.modelValue))

const buildingLocationId = ref('')
const storeyId = ref('')
const windConditionsId = ref('')
const roomInsulationId = ref('')
const roomTypeId = ref('')
const numberOfExternalWalls = ref('')
const glazingPercentBandId = ref('')
const glazingQualityId = ref('')
const length = ref('')
const width = ref('')
const ceilingHeight = ref('')

const toNumber = (value) => {
  if (typeof value === 'string') {
    if (value.trim() === '') return null
    const normalized = value.replace(',', '.')
    if (normalized !== value) {
      return Number(normalized)
    }
  }
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const roomArea = computed(() => {
  const lengthValue = toNumber(length.value)
  const widthValue = toNumber(width.value)
  if (lengthValue == null || widthValue == null) return null
  return lengthValue * widthValue
})

const roomAreaDisplay = computed(() => {
  if (roomArea.value == null) return ''
  return Math.round(roomArea.value * 10) / 10
})

const tables = computed(() => getRoomTables(props.market))

const buildOptions = (list, labels) =>
  list.map((item) => ({
    ...item,
    label: labels?.[item.id] || item.id,
  }))

const temperatureOptions = computed(() => buildOptions(tables.value.temp, copy.value.options?.temp))
const storeyOptions = computed(() =>
  buildOptions(tables.value.storeyValue, copy.value.options?.storeyValue)
)
const windOptions = computed(() =>
  buildOptions(tables.value.windInfluence, copy.value.options?.windInfluence)
)
const insulationOptions = computed(() =>
  buildOptions(tables.value.insulationValue, copy.value.options?.insulationValue)
)
const roomTypeOptions = computed(() =>
  buildOptions(tables.value.roomValue, copy.value.options?.roomValue)
)
const glazingBandOptions = computed(() =>
  buildOptions(tables.value.glazingValue, copy.value.options?.glazingValue)
)
const glazingQualityOptions = computed(() =>
  buildOptions(tables.value.qualityValue, copy.value.options?.qualityValue)
)

const selectedTemp = computed(
  () => temperatureOptions.value.find((option) => option.id === buildingLocationId.value) || null
)
const selectedStorey = computed(
  () => storeyOptions.value.find((option) => option.id === storeyId.value) || null
)
const selectedWind = computed(
  () => windOptions.value.find((option) => option.id === windConditionsId.value) || null
)
const selectedInsulation = computed(
  () => insulationOptions.value.find((option) => option.id === roomInsulationId.value) || null
)
const selectedRoomType = computed(
  () => roomTypeOptions.value.find((option) => option.id === roomTypeId.value) || null
)
const selectedGlazingBand = computed(
  () => glazingBandOptions.value.find((option) => option.id === glazingPercentBandId.value) || null
)
const selectedGlazingQuality = computed(
  () => glazingQualityOptions.value.find((option) => option.id === glazingQualityId.value) || null
)

const canCalculate = computed(() => {
  const requiredNumbers = [
    toNumber(length.value),
    toNumber(width.value),
    toNumber(ceilingHeight.value),
    toNumber(numberOfExternalWalls.value),
  ]
  const requiredText = [
    selectedTemp.value,
    selectedStorey.value,
    selectedWind.value,
    selectedInsulation.value,
    selectedRoomType.value,
    selectedGlazingBand.value,
    selectedGlazingQuality.value,
  ]
  return requiredNumbers.every((value) => value != null) && requiredText.every(Boolean)
})

const calculatedWattage = computed(() => {
  if (!canCalculate.value) return 0
  try {
    const result = calcRoomHeatLoss({
      inputs: {
        outdoorTemp_C: selectedTemp.value?.value,
        storeyKey: selectedStorey.value?.key,
        windInfluence: selectedWind.value?.value,
        uOuterWall_W_m2K: selectedInsulation.value?.uOuterWall,
        floorColIndex: selectedInsulation.value?.floorColIndex,
        ceilingColIndex: selectedInsulation.value?.ceilingColIndex,
        roomTemp_C: selectedRoomType.value?.roomTemp,
        ventilationChange: selectedRoomType.value?.ventilationChange,
        glazingPct: selectedGlazingBand.value?.value,
        uGlazing_W_m2K: selectedGlazingQuality.value?.value,
        numberOfExternalWalls: toNumber(numberOfExternalWalls.value),
        length_m: toNumber(length.value),
        width_m: toNumber(width.value),
        height_m: toNumber(ceilingHeight.value),
      },
      tables: { roofCeilingData: tables.value.roofCeilingData },
    })
    return Math.round(result?.totals?.total_W ?? 0)
  } catch {
    return 0
  }
})

const close = () => {
  if (!props.inline) {
    emit('update:modelValue', false)
  }
}

const useSelection = () => {
  trackEvent('calculator_room_use_click', {
    wattage: calculatedWattage.value,
    market: props.market,
  })
  emit('use', calculatedWattage.value)
  if (props.closeOnUse && !props.inline) {
    close()
  }
}

const resetForm = () => {
  trackEvent('calculator_room_clear_click', { market: props.market })
  buildingLocationId.value = ''
  storeyId.value = ''
  windConditionsId.value = ''
  roomInsulationId.value = ''
  roomTypeId.value = ''
  numberOfExternalWalls.value = ''
  glazingPercentBandId.value = ''
  glazingQualityId.value = ''
  length.value = ''
  width.value = ''
  ceilingHeight.value = ''
}

// const roomFieldRefs = [
//   ['buildingLocationId', buildingLocationId],
//   ['roomTypeId', roomTypeId],
//   ['storeyId', storeyId],
//   ['numberOfExternalWalls', numberOfExternalWalls],
//   ['windConditionsId', windConditionsId],
//   ['roomInsulationId', roomInsulationId],
//   ['glazingPercentBandId', glazingPercentBandId],
//   ['glazingQualityId', glazingQualityId],
// ]
// roomFieldRefs.forEach(([field, fieldRef]) => {
//   watch(fieldRef, (val) => {
//     if (!val) return
//     trackEvent('calculator_room_field_change', { field, value: val, market: props.market })
//   })
// })

watch(calculatedWattage, (val, old) => {
  if (val > 0 && (!old || old <= 0)) {
    trackEvent('calculator_room_result', {
      wattage: val,
      building_location: buildingLocationId.value,
      room_type: roomTypeId.value,
      storey: storeyId.value,
      external_walls: numberOfExternalWalls.value,
      wind: windConditionsId.value,
      insulation: roomInsulationId.value,
      glazing_area: glazingPercentBandId.value,
      glazing_quality: glazingQualityId.value,
      room_area: roomArea.value,
      ceiling_height: ceilingHeight.value,
      market: props.market,
    })
  }
})

const onKeydown = (event) => {
  if (!props.inline && event.key === 'Escape' && props.modelValue) {
    close()
  }
}

onMounted(() => {
  if (props.inline) {
    trackEvent('calculator_room_loaded', { market: props.market })
  } else {
    window.addEventListener('keydown', onKeydown)
  }
})

onUnmounted(() => {
  if (!props.inline) {
    window.removeEventListener('keydown', onKeydown)
  }
})
</script>

<template>
  <transition name="drawer-slide">
    <div v-if="isOpen" class="drawer" :class="{ 'drawer--inline': props.inline }">
      <div v-if="!props.inline" class="drawer__backdrop" @click="close"></div>
      <section
        class="drawer__panel"
        :class="{ 'drawer__panel--inline': props.inline }"
        role="dialog"
        :aria-modal="!props.inline"
        :aria-label="copy.title || 'Kalkylator för värmebehov'"
      >
        <header v-if="!isWidgetMode() || (!props.inline && props.showClose)" class="drawer__header">
          <div v-if="!isWidgetMode() || !props.inline">
            <h2 class="drawer__title">{{ copy.title || 'Kalkylator för värmebehov' }}</h2>
          </div>
          <button
            v-if="props.showClose"
            class="drawer__close"
            type="button"
            aria-label="Close"
            @click="close"
          >
            ×
          </button>
        </header>

        <p class="drawer__intro">
          {{
            copy.intro ||
            'Beräkna värmeförlusten i rummet för att kunna välja rätt produkt från Purmos sortiment av värmeavgivare.'
          }}
        </p>

        <div class="drawer__section">
          <p class="drawer__section-title">{{ copy.sections?.temps || 'Temperaturer' }}</p>
          <div class="drawer__fields">
            <label class="drawer__field">
              <span class="drawer__field-label">
                {{ copy.fields?.projectedDesignTemp || 'Dimensionerande utetemp' }}
              </span>
              <span class="drawer__select">
                <select v-model="buildingLocationId">
                  <option value="" disabled>
                    {{ copy.options?.select || 'Välj' }}
                  </option>
                  <option v-for="option in temperatureOptions" :key="option.id" :value="option.id">
                    {{ option.label }}
                  </option>
                </select>
              </span>
            </label>
            <label class="drawer__field">
              <span class="drawer__field-label">
                {{ copy.fields?.roomType || copy.fields?.roomPreset || 'Rumsprofil' }}
              </span>
              <span class="drawer__select">
                <select v-model="roomTypeId">
                  <option value="" disabled>
                    {{ copy.options?.select || 'Välj' }}
                  </option>
                  <option v-for="option in roomTypeOptions" :key="option.id" :value="option.id">
                    {{ option.label }}
                  </option>
                </select>
              </span>
            </label>
          </div>
        </div>

        <div class="drawer__section">
          <p class="drawer__section-title">{{ copy.sections?.placement || 'Placering' }}</p>
          <div class="drawer__fields">
            <label class="drawer__field">
              <span class="drawer__field-label">
                {{ copy.fields?.roomFloor || 'Våningsplan' }}
              </span>
              <span class="drawer__select">
                <select v-model="storeyId">
                  <option value="" disabled>
                    {{ copy.options?.select || 'Välj' }}
                  </option>
                  <option v-for="option in storeyOptions" :key="option.id" :value="option.id">
                    {{ option.label }}
                  </option>
                </select>
              </span>
            </label>
            <label class="drawer__field">
              <span class="drawer__field-label">{{
                copy.fields?.outerWalls || 'Ytterväggar'
              }}</span>
              <span class="drawer__select">
                <select v-model="numberOfExternalWalls">
                  <option value="" disabled>
                    {{ copy.options?.select || 'Välj' }}
                  </option>
                  <!-- <option value="0">0</option> -->
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                </select>
              </span>
            </label>
            <label class="drawer__field">
              <span class="drawer__field-label">
                {{ copy.fields?.windConditions || 'Vindförhållanden' }}
              </span>
              <span class="drawer__select">
                <select v-model="windConditionsId">
                  <option value="" disabled>
                    {{ copy.options?.select || 'Välj' }}
                  </option>
                  <option v-for="option in windOptions" :key="option.id" :value="option.id">
                    {{ option.label }}
                  </option>
                </select>
              </span>
            </label>
          </div>
        </div>

        <div class="drawer__section">
          <p class="drawer__section-title">{{ copy.sections?.insulation || 'Isolering' }}</p>
          <div class="drawer__fields">
            <label class="drawer__field">
              <span class="drawer__field-label">
                {{ copy.fields?.roomInsulation || 'Rumsisolering' }}
              </span>
              <span class="drawer__select">
                <select v-model="roomInsulationId">
                  <option value="" disabled>
                    {{ copy.options?.select || 'Välj' }}
                  </option>
                  <option v-for="option in insulationOptions" :key="option.id" :value="option.id">
                    {{ option.label }}
                  </option>
                </select>
              </span>
            </label>
            <label class="drawer__field">
              <span class="drawer__field-label">
                {{ copy.fields?.glazingArea || 'Glasyta' }}
              </span>
              <span class="drawer__select">
                <select v-model="glazingPercentBandId">
                  <option value="" disabled>
                    {{ copy.options?.select || 'Välj' }}
                  </option>
                  <option v-for="option in glazingBandOptions" :key="option.id" :value="option.id">
                    {{ option.label }}
                  </option>
                </select>
              </span>
            </label>
            <label class="drawer__field">
              <span class="drawer__field-label">
                {{ copy.fields?.glazingQuality || copy.fields?.windowInsulation || 'Glaskvalitet' }}
              </span>
              <span class="drawer__select">
                <select v-model="glazingQualityId">
                  <option value="" disabled>
                    {{ copy.options?.select || 'Välj' }}
                  </option>
                  <option
                    v-for="option in glazingQualityOptions"
                    :key="option.id"
                    :value="option.id"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </span>
            </label>
          </div>
        </div>

        <div class="drawer__section">
          <p class="drawer__section-title">
            {{ copy.sections?.dimensions || 'Rumsdimensioner' }}
          </p>
          <div class="drawer__fields drawer__fields--three">
            <label class="drawer__field">
              <span class="drawer__field-label">{{ copy.fields?.length || 'Längd' }}</span>
              <span class="drawer__input">
                <input v-model="length" type="number" min="0" step="0.01" />
                <span class="drawer__unit">{{ copy.units?.meter || 'm' }}</span>
              </span>
            </label>
            <label class="drawer__field">
              <span class="drawer__field-label">{{ copy.fields?.width || 'Bredd' }}</span>
              <span class="drawer__input">
                <input v-model="width" type="number" min="0" step="0.01" />
                <span class="drawer__unit">{{ copy.units?.meter || 'm' }}</span>
              </span>
            </label>
            <label class="drawer__field">
              <span class="drawer__field-label">
                {{ copy.fields?.ceilingHeight || 'Takhöjd' }}
              </span>
              <span class="drawer__input">
                <input v-model="ceilingHeight" type="number" min="0" step="0.01" />
                <span class="drawer__unit">{{ copy.units?.meter || 'm' }}</span>
              </span>
            </label>
          </div>
          <div class="drawer__fields">
            <label class="drawer__field">
              <span class="drawer__field-label">
                {{ copy.fields?.roomArea || 'Rumsarea' }}
              </span>
              <span class="drawer__input">
                <input :value="roomAreaDisplay" type="number" readonly />
                <span class="drawer__unit">{{ copy.units?.squareMeter || 'm²' }}</span>
              </span>
            </label>
          </div>
        </div>

        <div class="drawer__effect">
          <p class="drawer__section-title">{{ copy.sections?.result || 'Resultat' }}</p>
          <div class="drawer__result">
            <span class="drawer__result-value">{{ calculatedWattage }}</span>
            <span class="drawer__result-unit">{{ copy.units?.watts || 'W' }}</span>
          </div>
        </div>

        <footer v-if="!props.inline" class="drawer__footer">
          <action-button variant="secondary" @click="resetForm">
            <!-- <button class="drawer__button drawer__button--ghost" type="button" @click="resetForm"> -->
            {{ copy.clearButton || 'Clear all' }}
            <!-- </button> -->
          </action-button>
          <action-button @click="useSelection">
            {{ copy.useButton || 'Use' }}
          </action-button>
          <!-- <button class="drawer__button drawer__button--solid" type="button" >
          </button> -->
        </footer>
      </section>
    </div>
  </transition>
</template>

<style scoped>
.drawer {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  align-items: stretch;
}

.drawer--inline {
  position: relative;
  inset: auto;
  z-index: auto;
}

.drawer__backdrop {
  position: absolute;
  inset: 0;
  background: #0011138c;
  opacity: 1;
  transition: opacity 220ms ease;
}

.drawer__panel {
  position: relative;
  height: 100%;
  max-height: 100vh;
  width: min(520px, 94vw);
  background: #ffffff;
  box-shadow: 10px 0 30px rgba(0, 0, 0, 0.18);
  padding: 22px 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  transform: translateX(0);
  transition: transform 220ms ease;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.drawer__panel--inline {
  height: auto;
  max-height: none;
  width: 100%;
  box-shadow: none;
  overscroll-behavior: auto;
}

.drawer__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  position: sticky;
  top: -22px;
  margin: -22px -20px 0;
  padding: 22px 20px 12px;
  background: #ffffff;
  z-index: 1;
}

.drawer__title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #1c2b2e;
}

.drawer__close {
  margin-left: auto;
  border: none;
  background: transparent;
  color: #1c2b2e;
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
}

.drawer__intro {
  margin: 0;
  font-size: 1rem;
  color: #6b7b7f;
}

.drawer__section {
  display: grid;
  gap: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e4eaed;
}

.drawer__section-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1c2b2e;
}

.drawer__fields {
  display: grid;
  gap: 10px;
  align-items: start;
}

.drawer__fields--two {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.drawer__fields--three {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.drawer__glazing {
  display: grid;
  grid-template-columns: minmax(0, 120px) minmax(0, 1fr);
  gap: 8px;
  align-items: center;
}

.drawer__glazing .drawer__input {
  min-width: 0;
  width: 100%;
}

.drawer__glazing .drawer__radio-group {
  min-width: 0;
  flex-wrap: wrap;
}

.drawer__radio-group {
  display: inline-flex;
  align-items: center;
  gap: 12px;
}

.drawer__radio {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  color: #4e6b6e;
  cursor: pointer;
}

.drawer__radio-input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.drawer__radio-control {
  width: 14px;
  height: 14px;
  border: 1.5px solid #a6b4b6;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: border-color 150ms ease;
}

.drawer__radio-control::after {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #4e6b6e;
  opacity: 0;
  transition: opacity 150ms ease;
}

.drawer__radio-input:checked + .drawer__radio-control {
  border-color: #4e6b6e;
}

.drawer__radio-input:checked + .drawer__radio-control::after {
  opacity: 1;
}

.drawer__radio-input:focus-visible + .drawer__radio-control {
  box-shadow: 0 0 0 2px rgba(62, 144, 159, 0.3);
}

.drawer__hint {
  font-size: 0.75rem;
  color: #7b8b8e;
  min-height: 16px;
}

.drawer__field {
  display: grid;
  gap: 6px;
  font-size: 0.8rem;
  color: #5b6a6e;
}

.drawer__input {
  position: relative;
  display: grid;
  align-items: center;
}

.drawer__input input {
  width: 100%;
  height: 36px;
  padding: 8px 30px 8px 10px;
  outline: none;
  border: 1px solid var(--semantic-border-muted);
  border-radius: 2px;
  background: var(--semantic-bg-default);
  font-size: 0.85rem;
  color: var(--semantic-text-default);
}

.drawer__input input[readonly] {
  background: #f5f7f8;
  color: #7b8b8e;
}

.drawer__unit {
  position: absolute;
  right: 8px;
  font-size: 0.75rem;
  color: #98a6a9;
}

.drawer__select select {
  width: 100%;
  height: 36px;
  padding: 8px 40px 8px 10px;
  border: 1px solid var(--semantic-border-muted);
  border-radius: 2px;
  background-color: var(--semantic-bg-default);
  color: var(--semantic-text-default);
  font-size: 0.85rem;
  appearance: none;
  outline: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath d='M7 10l5 5 5-5' fill='none' stroke='%23888' stroke-width='2'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 18px 18px;
}

.drawer__effect {
  display: grid;
  justify-items: center;
  gap: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e4eaed;
}

.drawer__result {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  padding: 6px 14px;
  border: 1px solid #d9e2f3;
  border-radius: 2px;
  background: #ffffff;
  font-weight: 700;
  color: #0f2b2f;
}

.drawer__result-value {
  font-size: 1.2rem;
}

.drawer__result-unit {
  font-size: 0.8rem;
  color: #607276;
}

.drawer__footer {
  margin-top: auto;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.drawer-slide-enter-from .drawer__panel,
.drawer-slide-leave-to .drawer__panel {
  transform: translateX(-100%);
}

.drawer-slide-enter-from .drawer__backdrop,
.drawer-slide-leave-to .drawer__backdrop {
  opacity: 0;
}

@media (max-width: 640px) {
  .drawer__fields--two,
  .drawer__fields--three {
    grid-template-columns: 1fr;
  }

  .drawer__footer {
    grid-template-columns: 1fr;
  }
}
</style>
